'use client';
import { useState, useEffect } from 'react';
import { getPostsBySection, getVideos } from '@/lib/api';
import { Story, Video } from '@/lib/types';
import StoryCard from '@/components/ui/StoryCard';
import SectionHeader from '@/components/ui/SectionHeader';
import TabSwitcher from '@/components/ui/TabSwitcher';
import VideoCard from '@/components/ui/VideoCard';
import MostRead from '@/components/modules/MostRead';
import LoadMoreButton from '@/components/ui/LoadMoreButton';
import Pagination from '@/components/ui/Pagination';
import Link from 'next/link';

const subcats = [
  { id: 'All', label: 'All' },
  { id: 'Americas', label: 'Americas' },
  { id: 'Europe', label: 'Europe' },
  { id: 'Middle East', label: 'Middle East' },
  { id: 'Asia-Pacific', label: 'Asia-Pacific' },
  { id: 'South Asia', label: 'South Asia' },
  { id: 'Africa', label: 'Africa' },
  { id: 'Oceania', label: 'Oceania' },
];

export default function WorldPage() {
  const [activeSubcat, setActiveSubcat] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [worldStories, setWorldStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStories() {
      setLoading(true);
      try {
        const data = await getPostsBySection('World', activeSubcat);
        setWorldStories(data);
      } catch (error) {
        console.error('Error loading world stories:', error);
      } finally {
        setLoading(false);
      }
    }
    loadStories();
  }, [activeSubcat]);

  const [videos, setVideos] = useState<Video[]>([]);

  useEffect(() => {
    async function loadVideos() {
      try {
        const data = await getVideos('World');
        setVideos(data);
      } catch (error) {
        console.error('Error loading world videos:', error);
      }
    }
    loadVideos();
  }, []);

  const leadStory = worldStories[0];
  const secondaryGrid = worldStories.slice(1, 7);

  if (loading && worldStories.length === 0) {
    return <div className="max-w-[1400px] mx-auto px-5 py-20 text-center">Loading World News...</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5">
      {/* PAGE HEADER */}
      <div className="border-b border-[#D4D4D4] pb-6 pt-8 mb-0">
        <h1 className="font-['Playfair_Display',Georgia,serif] font-black text-[42px] lg:text-[52px] tracking-[-0.02em] text-[#0D0D0D] mb-3">WORLD</h1>
        <p className="font-['Source_Serif_4',Georgia,serif] italic text-[15px] text-[#2D2D2D] max-w-[580px] leading-[1.6]">
          Comprehensive coverage of international news — from conflict zones and diplomatic breakthroughs to the quiet forces reshaping the global order.
        </p>
      </div>

      {/* SUBCATEGORY NAV */}
      <div className="mt-0">
        <TabSwitcher tabs={subcats} activeTab={activeSubcat} onTabChange={setActiveSubcat} />
      </div>

      {/* LEAD STORY */}
      {leadStory && (
        <Link href={`/article/${leadStory.slug}`} className="grid grid-cols-1 md:grid-cols-[45%_55%] gap-0 mb-8 border border-[#D4D4D4] hover:opacity-80 transition-opacity cursor-pointer group block">
          <div 
            className={`${leadStory.imageBg?.startsWith('bg-') ? leadStory.imageBg : 'bg-gray-100'} aspect-video bg-cover bg-center bg-no-repeat`} 
            style={!leadStory.imageBg?.startsWith('bg-') ? { backgroundImage: `url(${leadStory.imageBg})` } : {}}
          />
          <div className="px-6 lg:px-8 py-6">
            <div className="mb-2">
              <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.1em] uppercase text-[#C8102E]">{leadStory.section}</span>
              {leadStory.subsection && <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[10px] text-[#6B6B6B] ml-2">/ {leadStory.subsection}</span>}
            </div>
            <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] block mb-2">{leadStory.publishedAt}</span>
            <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[24px] lg:text-[28px] leading-[1.2] mb-3 group-hover:text-[#C8102E] transition-colors">{leadStory.title}</h2>
            <p className="font-['Source_Serif_4',Georgia,serif] text-[14px] leading-[1.55] text-[#2D2D2D] mb-4">{leadStory.deck}</p>
            <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">By {leadStory.author} · {leadStory.readingTime} min read</p>
          </div>
        </Link>
      )}

      {/* SECONDARY GRID */}
      {secondaryGrid.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {secondaryGrid.map(story => <StoryCard key={story.id} story={story} size="medium" />)}
        </div>
      )}

      {/* LATEST + SIDEBAR */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 py-6 border-t border-[#D4D4D4]">
        <div>
          <SectionHeader title="Latest" />
          {worldStories.length > 0 ? worldStories.map((story, i) => (
            <Link key={story.id} href={`/article/${story.slug}`} className="flex items-start gap-4 py-3.5 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity cursor-pointer group">
              <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] text-[#6B6B6B] w-10 shrink-0 pt-0.5">{story.publishedAt.slice(11, 16) || 'Latest'}</span>
              <div className="flex-1">
                <h3 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[15px] leading-[1.35] group-hover:text-[#C8102E] transition-colors mb-1">{story.title}</h3>
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B]">{story.author} · {story.readingTime} min read</p>
              </div>
            </Link>
          )) : (
            <p className="py-10 text-center text-gray-500">No stories found in this section.</p>
          )}
          <LoadMoreButton />
        </div>
        <div className="lg:border-l lg:border-[#D4D4D4] lg:pl-8">
          <div className="mb-8">
            <div className="pb-2.5 border-b-[2.5px] border-[#0D0D0D] mb-4">
              <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Trending</h3>
            </div>
            {worldStories.slice(0, 5).map((story, i) => (
              <Link key={story.id} href={`/article/${story.slug}`} className="flex items-start gap-3 py-3 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity group">
                <span className="font-['Playfair_Display',Georgia,serif] font-bold text-[20px] text-[#D4D4D4] leading-none w-6 shrink-0">{i + 1}</span>
                <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[13px] leading-[1.35] group-hover:text-[#C8102E] transition-colors">{story.title}</h4>
              </Link>
            ))}
          </div>
          <MostRead />
        </div>
      </div>

      {/* ANALYSIS MODULE */}
      <section className="bg-[#F5F4F0] py-8 px-6 -mx-5 border-t border-[#D4D4D4]">
        <SectionHeader title="Analysis & Features" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {worldStories.filter(s => ['analysis', 'explainer', 'feature'].includes(s.label || '')).slice(0, 3).map(story => (
            <StoryCard key={story.id} story={story} size="medium" />
          ))}
        </div>
      </section>

      {/* VIDEO */}
      <section className="py-8 border-t border-[#D4D4D4]">
        <SectionHeader title="Video" moreLink="/video" moreLabel="More Video →" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {videos.map(video => <VideoCard key={video.id} video={video} />)}
        </div>
      </section>

      <Pagination currentPage={currentPage} totalPages={1} onPageChange={setCurrentPage} />
      <div className="py-8" />
    </div>
  );
}
